import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Palette, Upload, Trash2, Loader2, KeyRound,
  ShieldCheck, Timer, Heart, RefreshCw, CheckCircle2, X,
} from "lucide-react";
import { adminThemesApi } from "../lib/api";
import { cn } from "../lib/utils";

interface BgSlotData { slot: string; url: string | null; }

interface ToastItem { id: number; message: string; kind: "success" | "error"; }
let _tid = 0;
function useToasts() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const dismiss = useCallback((id: number) => {
    setToasts((p) => p.filter((t) => t.id !== id));
    const h = timers.current.get(id);
    if (h) { clearTimeout(h); timers.current.delete(id); }
  }, []);
  const add = useCallback((message: string, kind: "success" | "error") => {
    const id = ++_tid;
    setToasts((p) => [...p, { id, message, kind }]);
    timers.current.set(id, setTimeout(() => dismiss(id), 4000));
  }, [dismiss]);
  return { toasts, add, dismiss };
}

type LucideIcon = React.ComponentType<{ className?: string }>;
interface SlotMeta {
  labelKey: string;
  descKey: string;
  Icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  accentBorder: string;
  badge: string;
}

const SLOT_META: Record<string, SlotMeta> = {
  bg_guest_auth: {
    labelKey: "themes.slotGuestAuth",
    descKey: "themes.slotGuestAuthDesc",
    Icon: KeyRound,
    iconColor: "text-blue-400",
    iconBg: "bg-blue-500/10",
    accentBorder: "border-t-blue-400/60",
    badge: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  },
  bg_organizer_auth: {
    labelKey: "themes.slotOrganizerAuth",
    descKey: "themes.slotOrganizerAuthDesc",
    Icon: ShieldCheck,
    iconColor: "text-violet-400",
    iconBg: "bg-violet-500/10",
    accentBorder: "border-t-violet-400/60",
    badge: "bg-violet-500/10 text-violet-500 border-violet-500/20",
  },
  bg_guest_countdown: {
    labelKey: "themes.slotGuestCountdown",
    descKey: "themes.slotGuestCountdownDesc",
    Icon: Timer,
    iconColor: "text-amber-400",
    iconBg: "bg-amber-500/10",
    accentBorder: "border-t-amber-400/60",
    badge: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  },
  bg_guest_rsvp: {
    labelKey: "themes.slotGuestRsvp",
    descKey: "themes.slotGuestRsvpDesc",
    Icon: Heart,
    iconColor: "text-rose-400",
    iconBg: "bg-rose-500/10",
    accentBorder: "border-t-rose-400/60",
    badge: "bg-rose-500/10 text-rose-500 border-rose-500/20",
  },
};

const SLOT_ORDER = ["bg_guest_auth", "bg_organizer_auth", "bg_guest_countdown", "bg_guest_rsvp"];

function DeleteModal({
  label, onConfirm, onCancel, loading,
}: {
  label: string; onConfirm: () => void; onCancel: () => void; loading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <p className="text-sm font-semibold">{t("themes.deleteSlot")}</p>
        </div>
        <div className="px-5 py-4">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">"{label}"</span>{" "}
            {t("themes.confirmDelete")}
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-4">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-40"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-destructive text-white text-sm font-semibold hover:bg-destructive/90 transition-colors cursor-pointer disabled:opacity-40"
          >
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            <Trash2 className="h-3.5 w-3.5" />
            {t("common.delete")}
          </button>
        </div>
      </div>
    </div>
  );
}

function SlotCard({
  slot, url, onUploaded, onDeleted,
}: {
  slot: string; url: string | null;
  onUploaded: (slot: string, url: string) => void;
  onDeleted: (slot: string) => void;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const meta = SLOT_META[slot];
  const { Icon, iconColor, iconBg, accentBorder, badge, labelKey, descKey } = meta;

  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [localErr, setLocalErr]     = useState<string | null>(null);

  const processFile = useCallback(async (file: File) => {
    setLocalErr(null);
    setUploading(true);
    try {
      const res = await adminThemesApi.uploadBackground(slot, file);
      onUploaded(slot, res.url);
    } catch (err) {
      setLocalErr(err instanceof Error ? err.message : t("themes.uploadFailed"));
    } finally {
      setUploading(false);
    }
  }, [slot, onUploaded, t]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) processFile(file);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(true);
  }
  function handleDragLeave() { setIsDragOver(false); }
  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }

  async function handleDeleteConfirm() {
    setDeleting(true);
    try {
      await adminThemesApi.deleteBackground(slot);
      setShowDeleteModal(false);
      onDeleted(slot);
    } catch {
      setLocalErr(t("themes.deleteFailed"));
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  }

  const busy = uploading || deleting;

  return (
    <div className={cn(
      "group relative rounded-2xl border-2 border-border bg-card overflow-hidden",
      "flex flex-col transition-shadow hover:shadow-lg hover:shadow-black/5",
      accentBorder,
    )}>
      {/* Image / Drop Zone */}
      <div
        className={cn(
          "relative aspect-video overflow-hidden cursor-pointer select-none",
          isDragOver && "ring-2 ring-primary ring-inset",
        )}
        onClick={() => !busy && fileRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {url ? (
          <>
            <img
              src={url}
              alt={t(labelKey)}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />

            {/* Active badge */}
            <div className="absolute top-3 left-3">
              <span className={cn(
                "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border",
                badge,
              )}>
                <CheckCircle2 className="h-3 w-3" />
                {t("themes.active")}
              </span>
            </div>

            {/* Desktop hover overlay */}
            <div className={cn(
              "absolute inset-0 bg-black/55 flex items-center justify-center gap-3",
              "opacity-0 group-hover:opacity-100 transition-opacity duration-200",
            )}>
              <button
                onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                disabled={busy}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/90 text-sm font-semibold text-gray-900 hover:bg-white transition-colors disabled:opacity-50 cursor-pointer"
              >
                <Upload className="h-3.5 w-3.5" />
                {t("themes.change")}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setShowDeleteModal(true); }}
                disabled={busy}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-destructive/90 text-sm font-semibold text-white hover:bg-destructive transition-colors disabled:opacity-50 cursor-pointer"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t("themes.deleteSlot")}
              </button>
            </div>
          </>
        ) : (
          /* Empty state */
          <div className={cn(
            "absolute inset-0 flex flex-col items-center justify-center gap-3 p-6",
            "border-2 border-dashed transition-colors duration-200",
            isDragOver
              ? "border-primary bg-primary/5"
              : "border-border/60 bg-muted/30 hover:border-primary/40 hover:bg-primary/5",
          )}>
            <div className={cn("h-14 w-14 rounded-2xl flex items-center justify-center", iconBg)}>
              <Icon className={cn("h-7 w-7", iconColor)} />
            </div>
            <div className="text-center space-y-1">
              <p className="text-sm font-medium text-foreground">{t("themes.dragDrop")}</p>
              <p className="text-xs text-muted-foreground/70">{t("themes.fileHint")}</p>
            </div>
          </div>
        )}

        {/* Busy overlay */}
        {busy && (
          <div className="absolute inset-0 bg-background/70 flex items-center justify-center z-10">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
          </div>
        )}

        {/* Drag highlight overlay */}
        {isDragOver && url && (
          <div className="absolute inset-0 bg-primary/20 border-2 border-primary border-dashed z-10 flex items-center justify-center">
            <Upload className="h-8 w-8 text-primary" />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className={cn("h-8 w-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5", iconBg)}>
            <Icon className={cn("h-4 w-4", iconColor)} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">{t(labelKey)}</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{t(descKey)}</p>
          </div>
        </div>

        {localErr && (
          <p className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
            {localErr}
          </p>
        )}

        {/* Action row */}
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif"
            className="sr-only"
            onChange={handleFileChange}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border text-xs font-semibold transition-all cursor-pointer",
              "border-primary/30 text-primary hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed",
            )}
          >
            <Upload className="h-3.5 w-3.5" />
            {url ? t("themes.change") : t("themes.upload")}
          </button>
          {url && (
            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={busy}
              className="py-2 px-3 rounded-xl border border-border text-muted-foreground hover:text-destructive hover:border-destructive/30 text-xs font-semibold transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {showDeleteModal && (
          <DeleteModal
            label={t(labelKey)}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setShowDeleteModal(false)}
            loading={deleting}
          />
        )}
      </div>
    </div>
  );
}

export function ThemesPage() {
  const { t } = useTranslation();
  const { toasts, add, dismiss } = useToasts();
  const [slots, setSlots]   = useState<BgSlotData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback((cancelled?: { v: boolean }) => {
    setLoading(true);
    adminThemesApi.getBackgrounds()
      .then((res) => {
        if (cancelled?.v) return;
        const sorted = SLOT_ORDER.map((slot) => {
          const found = (res.data as BgSlotData[]).find((s) => s.slot === slot);
          return found ?? { slot, url: null };
        });
        setSlots(sorted);
        setError(null);
      })
      .catch((err) => {
        if (cancelled?.v) return;
        setError(err instanceof Error ? err.message : t("common.errorOccurred"));
      })
      .finally(() => { if (!cancelled?.v) setLoading(false); });
  }, [t]);

  useEffect(() => {
    const c = { v: false };
    load(c);
    return () => { c.v = true; };
  }, [load]);

  const handleUploaded = useCallback((slot: string, url: string) => {
    setSlots((p) => p.map((s) => s.slot === slot ? { ...s, url } : s));
    add(t("themes.uploaded"), "success");
  }, [add, t]);

  const handleDeleted = useCallback((slot: string) => {
    setSlots((p) => p.map((s) => s.slot === slot ? { ...s, url: null } : s));
    add(t("themes.deleted"), "success");
  }, [add, t]);

  const activeCount = slots.filter((s) => s.url).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif italic text-3xl text-foreground">{t("themes.backgrounds")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("themes.description")}</p>
        </div>
        <button
          onClick={() => load()}
          disabled={loading}
          className="shrink-0 p-2.5 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-primary/5 hover:border-primary/30 transition-all cursor-pointer disabled:opacity-40"
        >
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-8 text-center">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      ) : (
        <>
          {/* Stats strip */}
          <div className="rounded-2xl border border-border bg-card px-5 py-3.5 flex items-center gap-4">
            <Palette className="h-4 w-4 text-primary/60 shrink-0" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {t("themes.backgrounds")}
            </p>
            <div className="ml-auto flex items-center gap-1.5">
              {SLOT_ORDER.map((slot) => {
                const m = SLOT_META[slot];
                const active = slots.find((s) => s.slot === slot)?.url != null;
                return (
                  <div
                    key={slot}
                    className={cn(
                      "h-2 w-2 rounded-full transition-colors",
                      active ? m.iconColor.replace("text-", "bg-") : "bg-border",
                    )}
                    title={t(m.labelKey)}
                  />
                );
              })}
              <span className="ml-2 text-xs text-muted-foreground">
                {activeCount} / {SLOT_ORDER.length}
              </span>
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {slots.map((s) => (
              <SlotCard
                key={s.slot}
                slot={s.slot}
                url={s.url}
                onUploaded={handleUploaded}
                onDeleted={handleDeleted}
              />
            ))}
          </div>
        </>
      )}

      {/* Toasts */}
      <div className="fixed bottom-20 lg:bottom-6 right-4 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <button
            key={toast.id}
            onClick={() => dismiss(toast.id)}
            className={cn(
              "pointer-events-auto flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium shadow-xl cursor-pointer transition-all",
              toast.kind === "success"
                ? "bg-green-600 text-white"
                : "bg-destructive text-white",
            )}
          >
            {toast.kind === "success"
              ? <CheckCircle2 className="h-4 w-4 shrink-0" />
              : <X className="h-4 w-4 shrink-0" />}
            {toast.message}
          </button>
        ))}
      </div>
    </div>
  );
}
